import React, { useEffect, useRef, useState } from "react";
import { Button, Drawer, Space } from "antd";
import { RotateCcw } from "lucide-react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { REACT_APP_API } from "../../../../constants";
import { AN_COLORS, AN_MONO } from "../../../../design-system/tokens";
import { passwordErrorText, terminalStart } from "../../../../service/sshProxyService";
import { Note } from "../../../../pages/enduser/ssh-proxy/sshProxyUi";

// A browser terminal to one account on one endpoint, through its gateway.
//
// terminalStart issues a single-use ticket; the socket at REACT_APP_API (ws) +
// wsPath redeems it. Protocol (webterminal.go): we send {type:"data", data} and
// {type:"resize", rows, cols}; the server sends {type:"data"} and
// {type:"error"}, and closes the socket when the session ends. Reconnect asks
// for a new ticket, since a ticket opens one socket only.
//
// The ticket never goes to the console: it is worth a terminal for a minute.

const ANSI = { red: "\x1b[31m", dim: "\x1b[2m", reset: "\x1b[0m" };
const OPEN = 1; // WebSocket.OPEN, also used by the local-mode fake socket.
// Same flag as src/localMode/bootstrap.js; CRA inlines it at build time.
/* global process */
const LOCAL_MODE = process.env.REACT_APP_LOCAL_MODE === "true";

// The person's own error from the backend (403 / 429 carry {error}) wins:
// passwordErrorText alone would call every 403 an admin-role problem.
const startErrorText = (err) =>
  err?.response?.data?.error || passwordErrorText(err, "Couldn't start the terminal.");

// Local mode: the axios mock adapter can't intercept WebSockets, so this
// stands in for /console/ssh — a banner, a prompt, an echo; "exit" ends it.
function fakeSocket({ account, hostname }) {
  const prompt = `${account}@${String(hostname || "host").split(".")[0]}:~$ `;
  const sock = { readyState: 0, onopen: null, onmessage: null, onclose: null, onerror: null };
  const emit = (data) => sock.readyState === OPEN && sock.onmessage && sock.onmessage({ data: JSON.stringify({ type: "data", data }) });
  let line = "";
  setTimeout(() => {
    if (sock.readyState !== 0) return;
    sock.readyState = OPEN;
    if (sock.onopen) sock.onopen();
    emit(`Connecting to ${account}@${hostname} through the gateway...\r\n`);
    emit(`Welcome to ${hostname} (local mode: a fake terminal, nothing runs).\r\n\r\n`);
    emit(prompt);
  }, 400);
  sock.send = (raw) => {
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg?.type !== "data") return;
    for (const ch of String(msg.data || "")) {
      if (ch === "\r") {
        const cmd = line.trim();
        line = "";
        emit("\r\n");
        if (cmd === "exit") {
          emit("logout\r\n");
          sock.close();
          return;
        }
        if (cmd) emit(`${cmd.split(/\s+/)[0]}: command not found\r\n`);
        emit(prompt);
      } else if (ch === "\x7f") {
        if (line) {
          line = line.slice(0, -1);
          emit("\b \b");
        }
      } else if (ch === "\x03") {
        line = "";
        emit(`^C\r\n${prompt}`);
      } else if (ch >= " ") {
        line += ch;
        emit(ch);
      }
    }
  };
  sock.close = () => {
    if (sock.readyState === 3) return;
    sock.readyState = 3;
    setTimeout(() => sock.onclose && sock.onclose({ code: 1000, wasClean: true, reason: "session ended" }), 0);
  };
  return sock;
}

// The xterm and its socket. Mounted only while the drawer is open, so closing
// it releases everything. `session` changes on Retry / Reconnect: the same
// terminal is cleared and a new ticket and socket are used.
function TerminalSession({ instanceId, account, hostname, session, onPhase }) {
  const boxRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const socketRef = useRef(null);
  const onPhaseRef = useRef(onPhase);
  onPhaseRef.current = onPhase;

  const sendSize = () => {
    const socket = socketRef.current;
    const term = termRef.current;
    if (socket && socket.readyState === OPEN && term) {
      socket.send(JSON.stringify({ type: "resize", rows: term.rows, cols: term.cols }));
    }
  };
  const sendSizeRef = useRef(sendSize);
  sendSizeRef.current = sendSize;

  // The terminal itself: xterm + fit, as in MySqlTerminal.
  useEffect(() => {
    const term = new Terminal({
      cursorBlink: true,
      macOptionIsMeta: true,
      scrollback: 5000,
      theme: { background: "#000", foreground: "#f0f0f0" },
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    termRef.current = term;
    fitRef.current = fit;
    if (boxRef.current) term.open(boxRef.current);
    const refit = () => {
      if (!termRef.current?.element) return;
      try {
        fit.fit();
      } catch {
        return;
      }
      sendSizeRef.current();
    };
    const timer = setTimeout(refit, 50);
    const keys = term.onData((data) => {
      const socket = socketRef.current;
      if (socket && socket.readyState === OPEN) socket.send(JSON.stringify({ type: "data", data }));
    });
    const observer = new ResizeObserver(refit);
    if (boxRef.current) observer.observe(boxRef.current);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      keys.dispose();
      termRef.current = null;
      fitRef.current = null;
      term.dispose();
    };
  }, []);

  // One connection attempt: a ticket, then the socket that redeems it.
  useEffect(() => {
    let live = true;
    const term = termRef.current;
    if (!term) return undefined;
    term.reset();
    onPhaseRef.current("starting");

    terminalStart({
      instanceId: Number(instanceId),
      account,
      onSuccess: (res) => {
        if (!live) return;
        if (!res?.ticket) {
          onPhaseRef.current("error", "Couldn't start the terminal.");
          return;
        }
        const socket = LOCAL_MODE
          ? fakeSocket({ account, hostname: res.hostname || hostname })
          : new WebSocket(
              `${REACT_APP_API}`.replace(/\/+$/, "").replace(/^http/, "ws") + (res.wsPath || "/console/ssh") + "?ticket=" + encodeURIComponent(res.ticket),
            );
        socketRef.current = socket;

        socket.onopen = () => {
          if (!live) return;
          onPhaseRef.current("connected");
          try {
            fitRef.current?.fit();
          } catch {
            // Not laid out yet; the resize observer sends the size later.
          }
          sendSizeRef.current();
          term.focus();
        };
        socket.onmessage = (event) => {
          if (!live) return;
          let msg = null;
          try {
            msg = JSON.parse(event.data);
          } catch {
            msg = null;
          }
          if (msg && msg.type === "data") term.write(String(msg.data ?? ""));
          else if (msg && msg.type === "error") term.write(`\r\n${ANSI.red}${msg.data || "Error"}${ANSI.reset}\r\n`);
          else if (typeof event.data === "string") term.write(event.data);
        };
        // The browser logs a failed socket itself; nothing here logs the URL.
        socket.onerror = () => {};
        socket.onclose = () => {
          if (!live) return;
          socketRef.current = null;
          term.write(`\r\n${ANSI.dim}Session ended${ANSI.reset}\r\n`);
          onPhaseRef.current("ended");
        };
      },
      onFailure: (err) => {
        if (!live) return;
        onPhaseRef.current("error", startErrorText(err));
      },
    });

    return () => {
      live = false;
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        if (socket.readyState === 0 || socket.readyState === OPEN) socket.close();
      }
    };
  }, [instanceId, account, hostname, session]);

  return (
    <div style={{ flex: 1, minHeight: 0, background: "#000", padding: 8, overflow: "hidden" }}>
      <div ref={boxRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

export default function WebTerminalDrawer({ open, instanceId, account, hostname, onClose }) {
  const [session, setSession] = useState(0);
  const [phase, setPhase] = useState("starting");
  const [error, setError] = useState("");

  const onPhase = (next, text = "") => {
    setPhase(next);
    setError(next === "error" ? text : "");
  };
  const again = () => setSession((n) => n + 1);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="92vw"
      destroyOnClose
      title={
        <span style={{ fontFamily: AN_MONO, fontSize: 14, color: AN_COLORS.ink }}>
          {account}@{hostname}
        </span>
      }
      extra={
        <Space size={10}>
          <span style={{ fontSize: 12, color: phase === "connected" ? AN_COLORS.success : AN_COLORS.muted }}>
            {phase === "starting" ? "Connecting…" : phase === "connected" ? "Connected" : phase === "ended" ? "Session ended" : ""}
          </span>
          {phase === "ended" && (
            <Button size="small" type="primary" icon={<RotateCcw size={13} />} onClick={again}>
              Reconnect
            </Button>
          )}
        </Space>
      }
      styles={{ body: { padding: 0, display: "flex", flexDirection: "column", background: AN_COLORS.surface } }}
    >
      {phase === "error" && (
        <div style={{ padding: 12 }}>
          <Note tone="danger">
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span>{error}</span>
              <Button size="small" danger onClick={again}>
                Retry
              </Button>
            </div>
          </Note>
        </div>
      )}
      {open && (
        <TerminalSession instanceId={instanceId} account={account} hostname={hostname} session={session} onPhase={onPhase} />
      )}
    </Drawer>
  );
}
